/**
 * Storefront dataLayer tracking for GTM.
 *
 * Rebuilds the live theme's UKFDTracking layer. Event names and payloads match
 * what GTM container GTM-M8ZNC5S8 listens for; checkout events are not here -
 * they come from the Customer Events pixel ("BL | GTM - New").
 *
 * Config comes from snippets/tracking-config.liquid (theme settings → Tracking),
 * so UKFD / UKFD Trade run this file unchanged with their own values.
 *
 * `window.UKFDTracking.publish` is kept under its live name: the Advanced
 * Commerce console templates call it.
 */

/** Page views wait this long so consent defaults and GTM have booted first. */
const VIEW_DELAY_MS = 1000;

function config() {
  return window.themeTracking || {};
}

/**
 * Logs every event to the console when on. Clicks navigate away and take the
 * dataLayer with them, so logging is the only way to see what a click sent -
 * with DevTools' "Preserve log" the lines survive the navigation.
 *
 * Turn on in the browser console: localStorage.setItem("trackingDebug", "1")
 * Turn off:                      localStorage.removeItem("trackingDebug")
 * Always on in the theme editor.
 */
function debugEnabled() {
  if (window.Shopify?.designMode) return true;
  try {
    return window.localStorage.getItem("trackingDebug") === "1";
  } catch {
    return false;
  }
}

/**
 * Pushes one event to the dataLayer, with `sent_to` added.
 *
 * @param {string} event
 * @param {object} [params]
 */
export function publish(event, params = {}) {
  if (!event) return;

  const sendTo = config().sendTo;
  const payload = { event, ...(sendTo ? { sent_to: sendTo } : {}), ...params };

  window.dataLayer = window.dataLayer || [];

  // GA4 ecommerce objects merge in GTM's data model unless cleared first.
  if (payload.ecommerce) window.dataLayer.push({ ecommerce: null });

  window.dataLayer.push(payload);

  if (debugEnabled()) console.log("[tracking]", event, payload);
}

window.UKFDTracking = window.UKFDTracking || {};
window.UKFDTracking.publish = publish;

/* -----------------------------------------------------------------------------
   PAGE VIEWS
   Two markup formats, each fired once per element:
   - <script type="application/json" data-tracking-view="event"> from the theme
     (snippets/tracking-view.liquid)
   - [data-tracking-event][data-tracking-params] from the Advanced Commerce
     console templates (view_item_list / product_filters in plp.scriban)
----------------------------------------------------------------------------- */

function readJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function collectViews(scope) {
  const views = [];

  scope.querySelectorAll("script[data-tracking-view]").forEach((el) => {
    views.push({ el, event: el.dataset.trackingView, read: () => readJson(el.textContent) });
  });

  scope.querySelectorAll("[data-tracking-event][data-tracking-params]").forEach((el) => {
    views.push({
      el,
      event: el.dataset.trackingEvent,
      read: () => readJson(el.dataset.trackingParams),
    });
  });

  return views;
}

/**
 * Fires every not-yet-fired page view within `scope`.
 *
 * @param {ParentNode} scope
 * @param {{ delay?: number, only?: string[] }} [options]
 *   only - when set, events not in the list are marked fired but not sent
 */
export function initTrackingViews(scope = document, { delay = 0, only } = {}) {
  collectViews(scope).forEach(({ el, event, read }) => {
    if (!event || el.dataset.trackingFired) return;
    el.dataset.trackingFired = "true";

    if (only && !only.includes(event)) return;

    window.setTimeout(() => {
      // Removed or replaced (e.g. a colour swap) before the delay elapsed.
      if (!el.isConnected) return;

      const params = read();
      if (params) publish(event, params);
    }, delay);
  });
}

/* -----------------------------------------------------------------------------
   CART: add_to_cart, remove_from_cart, sample_add_to_cart
   Every add, quantity change, removal and clear goes through the cart
   controller (assets/cart.js), which hands each successful response here - one
   hook instead of the live theme's inline script per button.
----------------------------------------------------------------------------- */

/** Shopify prices on this store are ex VAT; tracked prices match what shoppers pay. */
const VAT_MULTIPLIER = 1.2;

function round2(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function isSampleLine(line) {
  return String(line?.variant_title || "").toLowerCase() === "sample";
}

/** A Shopify cart line as a GA4 item - same shape as snippets/tracking-item.liquid. */
function lineToItem(line, quantity) {
  const pence = line.final_price ?? line.price ?? 0;
  return {
    item_id: line.sku || null,
    item_name: line.product_title || null,
    affiliation: config().affiliation,
    item_brand: line.vendor || null,
    item_category: line.product_type || null,
    item_variant: line.variant_title || null,
    price: round2((pence / 100) * VAT_MULTIPLIER),
    quantity,
  };
}

function ecommerceFor(items) {
  return {
    currency: config().currency || "GBP",
    value: round2(items.reduce((sum, item) => sum + item.price * item.quantity, 0)),
    items,
  };
}

/** Line snapshot keyed by line key, seeded from the cart as rendered. */
let cartLines = new Map((config().cart?.items || []).map((line) => [line.key, line]));

function isCartLike(value) {
  return value && typeof value === "object" && Array.isArray(value.items);
}

/** Variant id → requested quantity, from any add payload shape the theme sends. */
function requestedQuantities(payload) {
  const quantities = new Map();
  const note = (id, qty) => {
    if (id === undefined || id === null || id === "") return;
    quantities.set(String(id), Number(qty) || 1);
  };

  if (typeof FormData !== "undefined" && payload instanceof FormData) {
    note(payload.get("id"), payload.get("quantity"));
    return quantities;
  }

  if (Array.isArray(payload?.items)) {
    payload.items.forEach((item) => note(item.id, item.quantity));
  } else if (payload && typeof payload === "object") {
    note(payload.id, payload.quantity);
  }

  return quantities;
}

function publishAdds(pairs) {
  const products = pairs.filter(({ line }) => !isSampleLine(line));
  const samples = pairs.filter(({ line }) => isSampleLine(line));

  if (products.length) {
    publish("add_to_cart", {
      ecommerce: ecommerceFor(products.map(({ line, quantity }) => lineToItem(line, quantity))),
    });
  }

  // Samples keep the live theme's own event and shape (no ecommerce object).
  if (samples.length) {
    publish("sample_add_to_cart", {
      currency: config().currency || "GBP",
      sample_items: samples.map(({ line, quantity }) => lineToItem(line, quantity)),
    });
  }
}

/**
 * Called by the cart controller after every successful request.
 *
 * @param {{ action: string, payload: any, responseData: any }} request
 */
export function trackCartResponse({ action, payload, responseData }) {
  if (!responseData || typeof responseData !== "object") return;

  if (action === "add") {
    const lines = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData.items)
        ? responseData.items
        : responseData.item
          ? [responseData.item]
          : responseData.variant_id
            ? [responseData]
            : [];

    const requested = requestedQuantities(payload);
    const pairs = lines.map((line) => ({
      line,
      quantity: requested.get(String(line.variant_id)) ?? line.quantity ?? 1,
    }));

    lines.forEach((line) => cartLines.set(line.key, line));
    publishAdds(pairs);
    return;
  }

  // change / update / clear / get all return (or imply) the whole cart.
  const nextCart = isCartLike(responseData.cart)
    ? responseData.cart
    : isCartLike(responseData)
      ? responseData
      : action === "clear"
        ? { items: [] }
        : null;
  if (!nextCart) return;

  const nextLines = new Map(nextCart.items.map((line) => [line.key, line]));

  // A refresh only re-syncs the snapshot - nothing was changed by the shopper.
  if (action !== "get") {
    const added = [];
    const removed = [];

    nextLines.forEach((line, key) => {
      const delta = line.quantity - (cartLines.get(key)?.quantity || 0);
      if (delta > 0) added.push({ line, quantity: delta });
    });

    cartLines.forEach((line, key) => {
      const delta = line.quantity - (nextLines.get(key)?.quantity || 0);
      if (delta > 0) removed.push({ line: nextLines.get(key) || line, quantity: delta });
    });

    if (added.length) {
      publish("add_to_cart", {
        ecommerce: ecommerceFor(added.map(({ line, quantity }) => lineToItem(line, quantity))),
      });
    }

    if (removed.length) {
      publish("remove_from_cart", {
        ecommerce: ecommerceFor(removed.map(({ line, quantity }) => lineToItem(line, quantity))),
      });
    }
  }

  cartLines = nextLines;
}

/* -----------------------------------------------------------------------------
   PRODUCT CLICKS: select_item / search_select
   Cards carry their GA4 item as data-tracking-select (theme product-card and the
   Advanced Commerce card templates). A click on any link inside the card counts;
   the wishlist, sample and quick view controls are buttons, so they don't.
----------------------------------------------------------------------------- */

const SEARCH_CONTEXT =
  "predictive-search, .predictive-search-results, [data-graphene-autocomplete], .graphene-autocomplete-overlay";

/**
 * The term the shopper searched for.
 *
 * From the predictive panel, that's whatever is typed into a search box - the
 * page may not be a search page at all, so the page's own term is empty. On the
 * search results page, the page's term is the fallback when nothing is typed.
 */
function currentSearchTerm(fromPredictive) {
  const typed = [...document.querySelectorAll('input[name="q"]')]
    .map((input) => (input.value || "").trim())
    .find(Boolean);

  return fromPredictive ? typed || config().searchTerm || "" : config().searchTerm || typed || "";
}

function onProductClick(event) {
  const link = event.target.closest?.("a[href]");
  const card = link?.closest("[data-tracking-select]");
  if (!card) return;

  const item = readJson(card.dataset.trackingSelect);
  if (!item) return;

  item.affiliation = item.affiliation || config().affiliation;
  item.quantity = 1;

  const fromPredictive = Boolean(link.closest(SEARCH_CONTEXT));
  const searchTerm = currentSearchTerm(fromPredictive);

  // The predictive panel opens on recommended products before anything is
  // typed. With no term, a click there is a product selection, not a search one.
  if (searchTerm && (config().pageType === "search" || fromPredictive)) {
    publish("search_select", {
      search_term: searchTerm,
      search_item_select: item.item_name,
    });
    return;
  }

  publish("select_item", { ecommerce: ecommerceFor([item]) });
}

/* -----------------------------------------------------------------------------
   CLICKS: header_nav_click, footer_click, inpage_nav_click, logo_click,
   contact_us_click, trade_account_btn_click, outbound_click, cta_click,
   carousel_brand_click

   The live theme hand-tagged every link with data-datalayer attributes across
   ~25 files. Here the event is worked out from where the click happened, using
   the theme's existing markup - so menus and sections the client edits in the
   theme editor are covered without anyone remembering to tag them. Payloads
   match the live attributes field for field.

   Anything can still be tagged by hand with data-track-click="event_name" and
   data-track-params='{"json": "payload"}' (HTML-escaped), which wins over the
   rules below.
----------------------------------------------------------------------------- */

const HEADER = "#header-group";
const DESKTOP_NAV = ".header-nav-row";
const MOBILE_NAV = ".mobile-menu-outer-wrapper";
const FOOTER_GROUP = ".footer-link-group";

function cleanText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function linkText(link) {
  return cleanText(link.innerText || link.getAttribute("aria-label") || link.title);
}

function isExternal(link) {
  try {
    return new URL(link.href, window.location.origin).hostname !== window.location.hostname;
  } catch {
    return false;
  }
}

function brand() {
  return config().affiliation;
}

/** The heading of the section a link sits in, or "N/A" - live's `header` field. */
function sectionHeading(el) {
  const section = el.closest(".shopify-section, section");
  const heading = section?.querySelector("h1, h2, h3, .heading-h1, .heading-h2, .heading-h3");
  return cleanText(heading?.textContent) || "N/A";
}

/** category / sub_category for a menu link, desktop or mobile. */
function navCategories(link) {
  // Desktop: the top-level item, then the dropdown column title if inside one.
  const topLevel = link.closest(".link-group");
  if (link.closest(DESKTOP_NAV) && topLevel) {
    const category = cleanText(
      topLevel.querySelector(".top-level-link-text")?.dataset.title || linkText(link),
    );
    const column = link.closest(".mega-menu-links-container");
    const columnTitle = cleanText(column?.querySelector(".dropdown-title")?.textContent);
    return { category, sub_category: column && columnTitle ? columnTitle : "N/A" };
  }

  // Mobile: a drill-down panel's title, then the accordion group title within it.
  const panel = link.closest("[data-nav-panel]");
  if (panel) {
    const category = cleanText(panel.querySelector(".mobile-nav-title")?.textContent);
    const group = link.closest(".accordion");
    const groupTitle = cleanText(group?.querySelector(".mobile-group-title")?.textContent);
    return { category: category || "N/A", sub_category: groupTitle || "N/A" };
  }

  // A plain top-level link in either menu.
  return { category: linkText(link), sub_category: "N/A" };
}

/**
 * Works out the event for a clicked link, or null if it isn't tracked.
 *
 * @param {HTMLAnchorElement} link
 * @returns {{ event: string, params: object } | null}
 */
function classifyClick(link) {
  const href = link.getAttribute("href") || "";
  const text = linkText(link);

  // Hand-tagged elements win.
  const tagged = link.closest("[data-track-click]");
  if (tagged) {
    return {
      event: tagged.dataset.trackClick,
      params: readJson(tagged.dataset.trackParams || "{}") || {},
    };
  }

  const inHeader = Boolean(link.closest(HEADER));
  const inFooter = Boolean(link.closest("footer, .site-footer--section"));

  if (href.startsWith("tel:")) {
    return {
      event: "contact_us_click",
      params: { click_text: "Call Us", section: inHeader ? "header" : inFooter ? "footer" : "page" },
    };
  }

  if (href.startsWith("mailto:") || href.startsWith("#") || href.startsWith("javascript:")) {
    return null;
  }

  if (inHeader && link.closest(".logo-container")) {
    return { event: "logo_click", params: { click_text: brand(), section: "header" } };
  }

  if (inHeader && /trade/i.test(href)) {
    return { event: "trade_account_btn_click", params: { click_text: text, click_url: href } };
  }

  if (link.closest(".breadcrumbs, [data-announcement-bar]")) {
    return { event: "inpage_nav_click", params: { click_text: text, click_url: href } };
  }

  const social = link.closest(".social-icons a, .social-icon-block a");
  if (social) {
    const network = cleanText(social.classList[0] || "").replace(/^\w/, (c) => c.toUpperCase());
    return {
      event: "outbound_click",
      params: { click_text: network, click_url: link.href, domain: network },
    };
  }

  if (link.closest(`${DESKTOP_NAV}, ${MOBILE_NAV}`)) {
    return {
      event: "header_nav_click",
      params: { click_text: text, click_url: href, brand: brand(), ...navCategories(link) },
    };
  }

  const footerGroup = link.closest(FOOTER_GROUP);
  if (footerGroup) {
    return {
      event: "footer_click",
      params: {
        category: cleanText(footerGroup.querySelector("h1, h2, h3, h4")?.textContent) || "N/A",
        click_text: text,
        click_url: href,
        brand: brand(),
      },
    };
  }

  if (link.closest(".brand-card")) {
    return {
      event: "carousel_brand_click",
      params: { click_text: text, header: sectionHeading(link) },
    };
  }

  if (isExternal(link)) {
    return { event: "outbound_click", params: { click_text: text, click_url: link.href } };
  }

  if (link.closest(".rte")) {
    return { event: "cta_click", params: { click_text: text, click_url: href } };
  }

  const isCta =
    link.matches('[class*="button-"], .links-container a, .cta a, .article-card a') &&
    link.closest("#MainContent");
  if (isCta) {
    return {
      event: "cta_click",
      params: { click_text: text, click_url: href, header: sectionHeading(link) },
    };
  }

  return null;
}

function onLinkClick(event) {
  const link = event.target.closest?.("a[href]");
  if (!link) return;

  // Product cards report select_item / search_select instead.
  if (link.closest("[data-tracking-select]")) return;

  const tracked = classifyClick(link);
  if (tracked?.event) publish(tracked.event, tracked.params);
}

/* -----------------------------------------------------------------------------
   BRAND CAROUSEL: carousel_brand_scroll
   The slider controller dispatches a non-bubbling `slidechange` on the slider
   element; a capture listener on document still receives it. The first event
   per slider only records where it started, so initialising isn't a "scroll".
----------------------------------------------------------------------------- */

const sliderPositions = new WeakMap();

function onSlideChange(event) {
  const slider = event.target;
  if (!(slider instanceof Element) || !slider.closest(".brand-slider")) return;

  const current = Number(event.detail?.firstSlideIndex ?? event.detail?.activeIndex);
  if (!Number.isFinite(current)) return;

  const previous = sliderPositions.get(slider);
  sliderPositions.set(slider, current);
  if (previous === undefined || previous === current) return;

  // Same wrap-around logic as the live carousel module.
  const last = slider.querySelectorAll(".blaze-track > *").length - 1;
  let direction = "next";
  if (previous === 0 && current === last) direction = "previous";
  else if (!(previous === last && current === 0) && current < previous) direction = "previous";

  publish("carousel_brand_scroll", { click: direction, header: sectionHeading(slider) });
}

/* -----------------------------------------------------------------------------
   FORMS: form_initiate, form_fallout, form_submit
   Ported from the live theme's tracking-forms module. Applies to any form (or
   wrapper) marked data-form-tracking="name".

   The contact form is a normal Shopify post that reloads the page, so a submit
   is only counted once the reloaded page shows the success state
   ([data-tracking-form-success]). Shopify Forms app forms post with fetch, so
   their success is read from the response instead, as on live.
----------------------------------------------------------------------------- */

const FORM = "[data-form-tracking]";
const formAttempts = new Map();

function fieldNameFrom(target) {
  const field = target?.closest?.("input, select, textarea");
  if (!field) return null;
  const raw =
    field.getAttribute("aria-label") ||
    field.getAttribute("name") ||
    field.id ||
    field.getAttribute("placeholder") ||
    field.tagName.toLowerCase();
  // Same normalising as live: contact[email] -> contactemail, "Your name" -> your_name.
  return (
    String(raw)
      .trim()
      .toLowerCase()
      .replace(/^custom#/, "")
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "")
      .replace(/^_+|_+$/g, "") || null
  );
}

function leadIdFor(formName) {
  const key = `form_lead_id_${formName}`;
  let leadId = null;
  try {
    leadId = window.sessionStorage.getItem(key);
    if (!leadId) {
      leadId = window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      window.sessionStorage.setItem(key, leadId);
    }
  } catch {
    // Storage blocked: still report the submit, just without a lead id.
  }
  return leadId;
}

function attemptFor(form) {
  if (!formAttempts.has(form)) {
    formAttempts.set(form, { active: false, submitted: false, lastField: null, lastFallout: null });
  }
  return formAttempts.get(form);
}

function onFormEngage(event) {
  const form = event.target.closest?.(FORM);
  if (!form) return;

  const attempt = attemptFor(form);
  const field = fieldNameFrom(event.target);
  if (field) attempt.lastField = field;
  if (attempt.active) return;

  // Returning to the field the shopper last dropped out on isn't a new attempt.
  if (field && attempt.lastFallout && field === attempt.lastFallout) return;

  attempt.active = true;
  attempt.submitted = false;
  attempt.lastFallout = null;
  publish("form_initiate", {
    form_name: form.dataset.formTracking || "unknown",
    ...(field ? { field_name: field } : {}),
  });
}

function falloutActiveForms(exceptForm = null) {
  formAttempts.forEach((attempt, form) => {
    if (form === exceptForm || !attempt.active || attempt.submitted) return;

    attempt.active = false;
    attempt.lastFallout = attempt.lastField;
    publish("form_fallout", {
      form_name: form.dataset.formTracking || "unknown",
      ...(attempt.lastField ? { field_name: attempt.lastField } : {}),
    });
  });
}

function onFormPointerOutside(event) {
  falloutActiveForms(event.target.closest?.(FORM) || null);
}

function onFormSubmit(event) {
  const form = event.target.closest?.(FORM);
  if (!form) return;

  const attempt = attemptFor(form);
  attempt.submitted = true;
  // Reserve the lead id now; form_submit fires on the reloaded success page.
  leadIdFor(form.dataset.formTracking || "unknown");
}

/** A native post that reloaded onto its success state. */
function publishFormSuccesses() {
  document.querySelectorAll("[data-tracking-form-success]").forEach((marker) => {
    if (marker.dataset.trackingFired) return;
    marker.dataset.trackingFired = "true";

    const formName = marker.dataset.trackingFormSuccess || "unknown";
    publish("form_submit", { form_name: formName, lead_id: leadIdFor(formName) });
    try {
      window.sessionStorage.removeItem(`form_lead_id_${formName}`);
    } catch {}
  });
}

/** Shopify Forms (app) posts with fetch; a 202 with no errors is a submit. */
function onShopifyFormsSuccess() {
  let reported = false;
  formAttempts.forEach((attempt, form) => {
    if (!attempt.active || attempt.submitted) return;
    attempt.submitted = true;
    attempt.active = false;
    reported = true;

    const formName = form.dataset.formTracking || "unknown";
    publish("form_submit", { form_name: formName, lead_id: leadIdFor(formName) });
    try {
      window.sessionStorage.removeItem(`form_lead_id_${formName}`);
    } catch {}
  });

  if (!reported) publish("form_submit", { form_name: "shopify_form", lead_id: leadIdFor("shopify_form") });
}

/* -----------------------------------------------------------------------------
   NEWSLETTER: newsletter_signup { hashed_email }
   - Footer newsletter: a native customer post that reloads. The email is held
     in sessionStorage for the reload (hashing can't finish before navigation)
     and hashed + removed once the success state renders.
   - Klaviyo: our back-in-stock opt-in and Klaviyo's own onsite forms post to
     a.klaviyo.com/client/subscriptions - with fetch or XMLHttpRequest - so both
     are watched, as live watched XMLHttpRequest. Back-in-stock requests alone
     (/client/back-in-stock-subscriptions) are not sign-ups.
----------------------------------------------------------------------------- */

const NEWSLETTER_EMAIL_KEY = "tracking_newsletter_email";
const KLAVIYO_SUBSCRIBE = /a\.klaviyo\.com\/client\/subscriptions\b/i;
const SHOPIFY_FORMS_SUBMIT = /forms\.shopifyapps\.com\/api\/v2\/form_submission/i;

async function sha256Hex(value) {
  const data = new TextEncoder().encode(String(value).trim().toLowerCase());
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function publishNewsletterSignup(email) {
  if (!email) return;
  try {
    publish("newsletter_signup", { hashed_email: await sha256Hex(email) });
  } catch {
    // No SubtleCrypto (non-secure context): don't send an unhashed email.
  }
}

function onNewsletterSubmit(event) {
  const form = event.target.closest?.("[data-newsletter-tracking]");
  if (!form) return;
  const email = form.querySelector('input[type="email"], input[name="contact[email]"]')?.value;
  if (!email) return;
  try {
    window.sessionStorage.setItem(NEWSLETTER_EMAIL_KEY, email.trim());
  } catch {}
}

function publishNewsletterSuccess() {
  if (!document.querySelector("[data-tracking-newsletter-success]")) return;
  let email = null;
  try {
    email = window.sessionStorage.getItem(NEWSLETTER_EMAIL_KEY);
    window.sessionStorage.removeItem(NEWSLETTER_EMAIL_KEY);
  } catch {}
  publishNewsletterSignup(email);
}

function emailFromBody(body) {
  if (!body) return null;
  if (typeof body === "string") {
    try {
      const json = JSON.parse(body);
      return (
        json?.data?.attributes?.profile?.data?.attributes?.email ||
        json?.data?.attributes?.email ||
        json?.email ||
        null
      );
    } catch {
      const match = body.match(/email=([^&]+)/i);
      return match ? decodeURIComponent(match[1]) : null;
    }
  }
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    return body.get("email") || body.get("Email") || body.get("EMAIL") || null;
  }
  return null;
}

function watchNetworkForSignups() {
  if (window.__themeTrackingNetworkWrapped) return;
  window.__themeTrackingNetworkWrapped = true;

  if (typeof window.fetch === "function") {
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const [input, init = {}] = args;
      const url = typeof input === "string" ? input : input?.url || "";
      const response = await originalFetch.apply(this, args);

      try {
        if (response.ok && KLAVIYO_SUBSCRIBE.test(url)) {
          publishNewsletterSignup(emailFromBody(init.body));
        } else if (response.status === 202 && SHOPIFY_FORMS_SUBMIT.test(url)) {
          response
            .clone()
            .json()
            .then((data) => {
              if (!data?.errors || Object.keys(data.errors).length === 0) onShopifyFormsSuccess();
            })
            .catch(() => {});
        }
      } catch {}

      return response;
    };
  }

  if (window.XMLHttpRequest) {
    const { open, send } = XMLHttpRequest.prototype;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__trackingUrl = url;
      return open.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function (body) {
      if (KLAVIYO_SUBSCRIBE.test(String(this.__trackingUrl || ""))) {
        const email = emailFromBody(body);
        this.addEventListener("loadend", () => {
          if (this.status >= 200 && this.status < 300) publishNewsletterSignup(email);
        });
      }
      return send.call(this, body);
    };
  }
}

/* -----------------------------------------------------------------------------
   BOOT
----------------------------------------------------------------------------- */

if (!window.__themeTrackingBound) {
  window.__themeTrackingBound = true;

  // Advanced Commerce listings already on the page count as their first render.
  const graphenesSeen = new WeakSet(document.querySelectorAll(".graphene-results--loaded"));

  initTrackingViews(document, { delay: VIEW_DELAY_MS });

  // Capture phase, so the push happens before the link navigates away.
  document.addEventListener("click", onProductClick, true);
  document.addEventListener("click", onLinkClick, true);

  // slidechange doesn't bubble; capture still reaches document.
  document.addEventListener("slidechange", onSlideChange, true);

  // Forms and newsletter.
  document.addEventListener("focusin", onFormEngage);
  document.addEventListener("pointerdown", onFormEngage);
  document.addEventListener("pointerdown", onFormPointerOutside, true);
  document.addEventListener("submit", onFormSubmit, true);
  document.addEventListener("submit", onNewsletterSubmit, true);
  window.addEventListener("blur", () => falloutActiveForms());
  watchNetworkForSignups();
  publishFormSuccesses();
  publishNewsletterSuccess();

  // The colour picker swaps the product section in place - a new product view.
  document.addEventListener("product:swapped", () => initTrackingViews(document));

  // Advanced Commerce re-renders the listing on every filter, sort and search
  // change. Matching the live theme: view_item_list on the first render only,
  // product_filters on every render after that.
  document.addEventListener("graphene:rendered", (event) => {
    const mount = event.detail?.mount;
    if (!mount) return;

    if (graphenesSeen.has(mount)) {
      initTrackingViews(mount, { only: ["product_filters"] });
    } else {
      graphenesSeen.add(mount);
      initTrackingViews(mount, { delay: VIEW_DELAY_MS });
    }
  });
}
